import { FilterMethod, ICustomFilter } from '../data-table';
import type { TaskSearchIn } from './task-search';

export type TaskTypeId =
    | 'all'
    | 'halt'
    | 'denial'
    | 'rejection'
    | 'records'
    | 'patient'
    | 'auth'
    | 'done';

/** Group keys used when All-types list is batched (includes uncategorized). */
export type TaskGroupId =
    | 'halt'
    | 'denial'
    | 'rejection'
    | 'records'
    | 'patient'
    | 'auth'
    | 'other';

export interface ITaskListFetchContext {
    typeId: TaskTypeId;
    mineOnly: boolean;
    searchTerm: string;
    /** subject = message subject LK; body = comment FTS (cached IDs). */
    searchIn: TaskSearchIn;
}

export interface ITaskTypeDefinition {
    id: TaskTypeId;
    /** Chip label in the type filter bar. */
    label: string;
    /** Batch group header label (All types view). */
    groupLabel?: string;
    /** When true, list uses filterOpen=1; Done uses filterOpen=0. */
    open: boolean;
    /**
     * Category name matchers (substring, case-insensitive).
     * Empty = no category filter (All / Done).
     */
    categoryMatchers: string[];
}

export const TASK_TYPE_DEFINITIONS: ITaskTypeDefinition[] = [
    { id: 'all', label: 'All types', open: true, categoryMatchers: [] },
    {
        id: 'halt',
        label: 'Halts',
        groupLabel: 'Halts',
        open: true,
        categoryMatchers: [
            'Claim Halted',
            'Invalid Claim - Interface',
            'Invalid Claim - Billing',
            'Invalid Claim - Clinical',
        ],
    },
    {
        id: 'denial',
        label: 'Denials',
        groupLabel: 'Denials',
        open: true,
        categoryMatchers: ['Claim Denied - Rules Matched'],
    },
    {
        id: 'rejection',
        label: 'Rejections',
        groupLabel: 'Rejections',
        open: true,
        categoryMatchers: ['Claim Rejected - Rules Matched'],
    },
    {
        id: 'records',
        label: 'Records',
        groupLabel: 'Records',
        open: true,
        categoryMatchers: ['Missing Medical Records'],
    },
    {
        id: 'patient',
        label: 'Patient info',
        groupLabel: 'Patient info',
        open: true,
        categoryMatchers: ['Inactive/Invalid Subscriber', 'Payers Swapped'],
    },
    {
        id: 'auth',
        label: 'Authorizations',
        groupLabel: 'Authorizations',
        open: true,
        categoryMatchers: ['Missing Prior Authorization'],
    },
    { id: 'done', label: 'Done', open: false, categoryMatchers: [] },
];

const OPEN_TASK_GROUP_IDS: TaskGroupId[] = [
    'halt',
    'denial',
    'rejection',
    'records',
    'patient',
    'auth',
];

export interface IMessageCategoryRef {
    id?: string;
    name?: string;
}

export function categoryMatches(name: string | undefined, matcher: string): boolean {
    if (!name) {
        return false;
    }
    return name.toLowerCase().includes(matcher.toLowerCase());
}

export function resolveCategoryIdsForType(
    categories: IMessageCategoryRef[],
    typeId: TaskTypeId,
): string[] {
    const def = TASK_TYPE_DEFINITIONS.find(t => t.id === typeId);
    if (!def || def.categoryMatchers.length === 0) {
        return [];
    }
    return categories
        .filter(cat => def.categoryMatchers.some(m => categoryMatches(cat.name, m)))
        .map(cat => cat.id)
        .filter((id): id is string => !!id);
}

/** Which known task type a row belongs to (for filtering). Unknown → null. */
export function resolveTaskTypeFromCategoryNames(categoryNames: string[]): TaskTypeId | null {
    const openTypes = TASK_TYPE_DEFINITIONS.filter(t => t.id !== 'all' && t.id !== 'done');
    for (const def of openTypes) {
        if (categoryNames.some(name => def.categoryMatchers.some(m => categoryMatches(name, m)))) {
            return def.id;
        }
    }
    return null;
}

/** Batch group for All-types view — unknown categories go to Other. */
export function resolveTaskGroupId(categoryNames: string[]): TaskGroupId {
    const typeId = resolveTaskTypeFromCategoryNames(categoryNames);
    if (typeId && OPEN_TASK_GROUP_IDS.includes(typeId as TaskGroupId)) {
        return typeId as TaskGroupId;
    }
    return 'other';
}

export function buildOpenFilter(open: boolean): ICustomFilter {
    return {
        filterId: 'filterOpen',
        filterMethod: FilterMethod.EQ,
        filterType: 'String',
        value: open ? '1' : '0',
    };
}

export function buildCategoryFilters(categoryIds: string[]): ICustomFilter[] {
    return categoryIds.map(id => ({
        filterId: 'filterMessageCategoryId',
        filterMethod: FilterMethod.EQ,
        filterType: 'Select',
        value: { label: id, value: id },
    }));
}

export function buildUserFilter(userId: string): ICustomFilter {
    return {
        filterId: 'filterUserId',
        filterMethod: FilterMethod.EQ,
        filterType: 'Select',
        value: { label: 'Self', value: userId },
    };
}

export function buildSubjectFilter(term: string): ICustomFilter {
    return {
        filterId: 'filterSubject',
        filterMethod: FilterMethod.LK,
        filterType: 'String',
        value: term.trim(),
    };
}

/**
 * Sentinel filterId value — no real message can match it, so the API returns an
 * empty result set. `reason` keeps network requests self-explanatory.
 */
export function buildNoMatchFilter(reason: 'category' | 'search'): ICustomFilter {
    return {
        filterId: 'filterId',
        filterMethod: FilterMethod.EQ,
        filterType: 'String',
        value: `__no_${reason}_match__`,
    };
}

export function buildMessageIdFilters(messageIds: string[]): ICustomFilter[] {
    return messageIds.map(id => ({
        filterId: 'filterId',
        filterMethod: FilterMethod.EQ,
        filterType: 'String',
        value: id,
    }));
}

/**
 * Filters for a task type chip (+ optional My tasks).
 * Does not include search — search is resolved separately (subject ∪ comment body).
 */
export function buildTaskTypeFilters(
    typeId: TaskTypeId,
    categories: IMessageCategoryRef[],
    options?: { mineOnly?: boolean; currentUserId?: string },
): ICustomFilter[] {
    const def = TASK_TYPE_DEFINITIONS.find(t => t.id === typeId) || TASK_TYPE_DEFINITIONS[0];
    const filters: ICustomFilter[] = [buildOpenFilter(def.open)];
    if (def.categoryMatchers.length > 0) {
        const categoryIds = resolveCategoryIdsForType(categories, typeId);
        if (categoryIds.length) {
            filters.push(...buildCategoryFilters(categoryIds));
        } else {
            // Matchers configured but none resolved — force empty result set
            filters.push(buildNoMatchFilter('category'));
        }
    }
    if (options?.mineOnly && options.currentUserId) {
        filters.push(buildUserFilter(options.currentUserId));
    }
    return filters;
}

/** Sort messages so known types cluster, then other. */
export function sortMessagesForGrouping<T extends { categories?: string[] }>(messages: T[]): T[] {
    const rank = (cats: string[] | undefined): number => {
        const g = resolveTaskGroupId(cats || []);
        const idx = OPEN_TASK_GROUP_IDS.indexOf(g);
        return idx >= 0 ? idx : OPEN_TASK_GROUP_IDS.length;
    };
    return [...messages].sort((a, b) => rank(a.categories) - rank(b.categories));
}

export function getGroupHeaderLabel(groupId: TaskGroupId): string {
    if (groupId === 'other') {
        return 'Other types';
    }
    const def = TASK_TYPE_DEFINITIONS.find(t => t.id === groupId);
    return def?.groupLabel || def?.label || groupId;
}
